import {observer} from 'mobx-react';
import React from 'react';
import {screenResizeStore} from '../../../../global/store/screen-resize-store';
import {normalizeThickness} from '../moulding-filters';
import {MouldingCustomization} from '../../../i';
import ReactDOMServer from 'react-dom/server';
import RecycledIcon from './recycled-icon';
import s from '../../../s';
import {Portal, webp} from '../../../../util';
import {OwnedPopoverScope} from '../../shared/owned-popovers/use-owned-popovers';
import {mouldingOrder} from '../../../selection/moulding';
import {useOptionList} from '../../option-list/option-list';

type MouldingsListProps = {
    collapsed: boolean;
    selectedFinish: string;
    selectedColor?: string;
    selectedThickness?: string;
    collapsedNumber: number;
    save: (id: number) => void;
    isModal?: boolean;
    showNames: boolean;
};

export const MouldingsList = observer((props: MouldingsListProps) => {
    const {isModal, collapsed, selectedFinish, selectedColor = '-1', selectedThickness = '-1', collapsedNumber, showNames, save} = props;

    const isDesktopCaf = s.prod.IsCAF && screenResizeStore.width >= 1200;
    const filtered = s.prod.IsCAF
        ? s.prod.mouldings.filter(m => (isDesktopCaf ? (selectedColor === '-1' || m.MouldingFinish === selectedColor) && (selectedThickness === '-1' || normalizeThickness(m.thickness) === Number(selectedThickness)) : selectedFinish === '-1' || m.fpc_name === selectedFinish))
        : s.prod.mouldings;
    // the tile order, the Fast Ship icon's moulding and the Trending one (selection/moulding.ts)
    const {hasFastShip, trendingMoulding, sortedMouldings} = mouldingOrder(filtered);
    const list = s.pcs.IsCAF ? [...sortedMouldings].sort((a, b) => a.fpc_sort - b.fpc_sort || a.SortOrder - b.SortOrder || a.title.localeCompare(b.title)).slice(0, collapsedNumber) : sortedMouldings.slice(0, collapsedNumber);
    const optionList = useOptionList(
        list.map(m => ({domId: `moulding-${m.id}`, value: m.id})),
        s.ps.moulding_id,
        save,
    );

    return (
        <OwnedPopoverScope
            className={`moulding-prod${showNames ? ' moulding-prod--names' : ''}`}
            role="listbox"
            aria-label="Select frame mouldings"
            style={{justifyContent: isModal ? 'space-around' : ''}}
            syncKey={list.map(m => m.id).join(',')}
            onKeyDown={optionList.onKeyDown}
        >
            {list.map((m: MouldingCustomization, i: number) => {
                const opt = optionList.option(m.id);
                return (
                    <div
                        data-bs-toggle={window.innerWidth > 1200 ? 'popover' : ''}
                        data-bs-placement="top"
                        data-bs-html="true"
                        data-bs-trigger="hover focus"
                        data-bs-content={`<div class="prod-img-zoom"><img loading="lazy" src="https://${Portal?.CDNDomain}/images/entities/moulding/${m.th5 ? `${m.id}_th5` : `${m.name.toLowerCase()}_thumb_270`}.png" alt="" />${
                            m.IsRecycled
                                ? ReactDOMServer.renderToStaticMarkup(
                                      <RecycledIcon
                                          width={90}
                                          top={'45%'}
                                          left={'70px'}
                                      />,
                                  )
                                : ''
                        }</div>`}
                        id={opt.id}
                        key={m.id}
                        title={m.title}
                        aria-label={`Select ${m.title} moulding`}
                        aria-selected={opt.ariaSelected}
                        className={`cust-entity${s.ps.moulding_id === m.id ? ' active' : ''}${!collapsed ? ' active-bold' : ''}`}
                        onClick={opt.onActivate}
                        onKeyDown={opt.onKeyDown}
                        data-img={`https://${Portal?.CDNDomain}/images/entities/moulding/${m.name.toLowerCase()}_thumb_270.png`}
                        role="option"
                        tabIndex={opt.tabIndex}
                    >
                        <div style={{position: 'relative'}}>
                            <picture>
                                <source
                                    type="image/webp"
                                    srcSet={webp(`https://${Portal?.CDNDomain}/images/entities/moulding/${m.name.toLowerCase()}_thumb_90.png`)}
                                />
                                <img
                                    className="moulding-img"
                                    data-img={`https://${Portal?.CDNDomain}/images/entities/moulding/${m.name.toLowerCase()}_thumb_270.png`}
                                    loading="lazy"
                                    src={`https://${Portal?.CDNDomain}/images/entities/moulding/${m.name.toLowerCase()}_thumb_90.png`}
                                    alt={m.title}
                                    height={70}
                                    width={70}
                                />
                            </picture>

                            {m.IsRecycled != undefined && m.IsRecycled && (
                                <RecycledIcon
                                    width={20}
                                    top={'10px'}
                                    left={'10px'}
                                />
                            )}

                            {showNames && <div className="moulding-name">{m.title}</div>}
                        </div>

                        {s.__fast_ship_orig && s.isRetail && s.prod.MouldingID === m.id && (
                            <div className={`prod-fast-shipping fast-shipping-${Portal?.PortalID}`}>
                                <i
                                    className={Portal?.PortalID !== 3 ? `fas fa-shipping-fast` : `fas fa-cart-shopping-fast`}
                                    aria-hidden={true}
                                ></i>
                            </div>
                        )}

                        {!hasFastShip && m === trendingMoulding && m.id !== s.prod.MouldingID && (
                            <div
                                className="prod-moulding-trending"
                                aria-label="Trending"
                            >
                                <i
                                    className="fa-regular fa-fire-flame-curved"
                                    aria-hidden={true}
                                ></i>
                                <span>Trending</span>
                            </div>
                        )}
                    </div>
                );
            })}
        </OwnedPopoverScope>
    );
});
